import { useId, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isApiError } from '../api/client';
import { lab, type AdoptedHold, type LabRoll } from '../api/lab';
import { queryKeys } from '../api/queryKeys';
import { NegativesActions } from '../components/NegativesActions';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useAdminCall } from '../hooks/useAdminCall';
import { usePageTitle } from '../hooks/usePageTitle';
import type { RollProcess } from '../types/roll';
import { describeNegatives, describeRoll, PROCESS_LABELS, shortDate, stageLabel } from '../utils/rollDetails';
import styles from './AdminLabQueuePage.module.css';

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

const uploadLink = (orderId: string) => `/admin/orders/${encodeURIComponent(orderId)}/upload`;

/** "Kodak Portra 400 · 35mm · Develop only": a queued roll in brief. */
function rollSummary(roll: LabRoll): string {
  const film = roll.film_brand && roll.film_stock_name ? `${roll.film_brand} ${roll.film_stock_name}` : roll.film_stock_name;
  return [film, describeRoll({ format: roll.format, kind: roll.kind })].filter(Boolean).join(' · ');
}

function Customer({ roll }: { roll: Pick<LabRoll, 'customer_name' | 'order_id' | 'order_number'> }) {
  return (
    <>
      <Link to={uploadLink(roll.order_id)}>{roll.order_number ?? roll.order_id}</Link>
      {roll.customer_name && <span className={styles.muted}> · {roll.customer_name}</span>}
    </>
  );
}

/** Find a roll by the number on its twin-check label. */
function FindByNumber() {
  const call = useAdminCall();
  const id = useId();
  const [typed, setTyped] = useState('');
  const [number, setNumber] = useState<string | null>(null);
  const found = useQuery({
    queryKey: queryKeys.admin.labNumber(number),
    queryFn: () => call(() => lab.byNumber(number!)),
    enabled: !!number,
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (typed.trim()) setNumber(typed.trim());
  }

  return (
    <section className={styles.section} aria-labelledby={`${id}-heading`}>
      <h2 id={`${id}-heading`} className={styles.sectionHeading}>
        Find a roll by lab number
      </h2>
      <form className={styles.find} onSubmit={submit}>
        <label htmlFor={`${id}-number`} className={styles.label}>
          Lab number
        </label>
        <input
          id={`${id}-number`}
          className={styles.input}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          autoComplete="off"
          spellCheck={false}
          maxLength={16}
        />
        <button type="submit" className={styles.secondaryBtn}>
          Find
        </button>
      </form>
      {number && (
        <div role="region" aria-live="polite" aria-label={`Rolls numbered ${number}`} className={styles.results}>
          {found.isLoading && <p className={styles.muted}>Looking…</p>}
          {found.error && (
            <p role="alert" className={styles.error}>
              {(isApiError(found.error) && found.error.serverMessage) || 'The search failed.'}
            </p>
          )}
          {found.data && found.data.rolls.length === 0 && (
            <p className={styles.muted}>No roll has had number {found.data.number} in the last 180 days.</p>
          )}
          {found.data && found.data.rolls.length > 0 && (
            <ul className={styles.list}>
              {found.data.rolls.map((r) => {
                const negatives = r.roll.tracking ? describeNegatives(r.roll.tracking.negatives) : null;
                return (
                  <li key={r.roll.roll_id}>
                    <strong>{r.roll.roll_name}</strong> ·{' '}
                    <Customer roll={{ order_id: r.order_id, order_number: r.order_number, customer_name: r.customer_name }} />
                    <span className={styles.muted}>
                      {' '}
                      · booked in {shortDate(r.claimed_at)}
                      {stageLabel(r.roll) && ` · ${stageLabel(r.roll)}`}
                      {negatives && ` · negatives ${negatives.text}`}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

/** Rolls to develop in one process, with a checkbox each and one for the lot. */
function DevelopGroup({
  process,
  rolls,
  selected,
  onToggle,
}: {
  process: RollProcess | null;
  rolls: LabRoll[];
  selected: Set<string>;
  onToggle: (rollIds: string[], on: boolean) => void;
}) {
  const id = useId();
  const label = process ? PROCESS_LABELS[process] : 'Process not known';
  const all = rolls.every((r) => selected.has(r.roll_id));
  return (
    <div className={styles.group}>
      <h3 id={`${id}-heading`} className={styles.groupHeading}>
        {label} · {plural(rolls.length, 'roll')}
      </h3>
      <label className={styles.checkbox}>
        <input type="checkbox" checked={all} onChange={(e) => onToggle(rolls.map((r) => r.roll_id), e.target.checked)} />
        Select all {label}
      </label>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
      <div className={styles.tableWrapper} tabIndex={0} role="region" aria-labelledby={`${id}-heading`}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th scope="col">
                <span className={styles.srOnly}>Select</span>
              </th>
              <th scope="col">Lab no.</th>
              <th scope="col">Film</th>
              <th scope="col">Order</th>
              <th scope="col">Received</th>
              <th scope="col">Special instructions</th>
            </tr>
          </thead>
          <tbody>
            {rolls.map((roll) => (
              <tr key={roll.roll_id} className={selected.has(roll.roll_id) ? styles.selectedRow : undefined}>
                <td>
                  <input
                    type="checkbox"
                    aria-label={`Select ${roll.roll_name}`}
                    checked={selected.has(roll.roll_id)}
                    onChange={(e) => onToggle([roll.roll_id], e.target.checked)}
                  />
                </td>
                <td className={styles.number}>{roll.lab_roll_number ?? '—'}</td>
                <td>{rollSummary(roll) || roll.roll_name}</td>
                <td>
                  <Customer roll={roll} />
                </td>
                <td>{roll.since ? shortDate(roll.since) : '—'}</td>
                <td>{roll.special_instructions && <strong className={styles.instructions}>{roll.special_instructions}</strong>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Track the holds from before tracking existed: look first, then do it. */
function OlderHolds({ onDone }: { onDone: (message: string) => void }) {
  const call = useAdminCall();
  const queryClient = useQueryClient();
  const id = useId();
  const [found, setFound] = useState<{ rolls: AdoptedHold[]; orders_without_rolls: string[] } | null>(null);
  const adopt = useMutation({
    mutationFn: (dryRun: boolean) => call(() => lab.adoptLegacy(dryRun)),
    onSuccess: (result) => {
      if (result.dry_run) {
        setFound(result);
        return;
      }
      setFound(null);
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.labQueue() });
      const adopted = result.rolls.filter((r) => r.outcome === 'adopted').length;
      onDone(`Now tracking ${plural(adopted, 'older hold')}.`);
    },
  });

  return (
    <section className={styles.section} aria-labelledby={`${id}-heading`}>
      <h2 id={`${id}-heading`} className={styles.sectionHeading}>
        Holds from before tracking
      </h2>
      <p className={styles.muted}>
        Orders made ready in the last 90 days whose customer asked to hold their negatives. Tracking them puts them in
        the list above, with 90 days from when their scans were ready.
      </p>
      {!found && (
        <button type="button" className={styles.secondaryBtn} onClick={() => adopt.mutate(true)} disabled={adopt.isPending}>
          {adopt.isPending ? 'Checking…' : 'Check for older holds'}
        </button>
      )}
      {found && (
        <div className={styles.results}>
          {found.rolls.length === 0 ? (
            <p>There are no older holds to track.</p>
          ) : (
            <>
              <ul className={styles.list}>
                {found.rolls.map((r) => (
                  <li key={r.roll_id}>
                    <strong>{r.roll_name}</strong> · <Link to={uploadLink(r.order_id)}>{r.order_number}</Link>
                    <span className={styles.muted}> · held until {shortDate(r.held_until)}</span>
                  </li>
                ))}
              </ul>
              <button type="button" className={styles.primaryBtn} onClick={() => adopt.mutate(false)} disabled={adopt.isPending}>
                {adopt.isPending ? 'Tracking…' : `Track ${plural(found.rolls.length, 'hold')}`}
              </button>
            </>
          )}
          {found.orders_without_rolls.length > 0 && (
            <p className={styles.muted}>
              These have no rolls to track (their scans predate rolls): {found.orders_without_rolls.join(', ')}. Add a roll's
              details on the upload page to track one.
            </p>
          )}
        </div>
      )}
      {adopt.error && (
        <p role="alert" className={styles.error}>
          {(isApiError(adopt.error) && adopt.error.serverMessage) || 'That didn’t work. Try again.'}
        </p>
      )}
    </section>
  );
}

/**
 * The lab's work: rolls to develop (by process, for the tanks), rolls to
 * scan (by order), negatives held for a companion order (overdue first), a
 * lab-number search, and tracking the holds from before tracking.
 */
export function AdminLabQueuePage() {
  usePageTitle('Admin — Lab');
  const { logout } = useAdminAuth();
  const call = useAdminCall();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [announcement, setAnnouncement] = useState('');

  const queue = useQuery({ queryKey: queryKeys.admin.labQueue(), queryFn: () => call(lab.queue) });
  const data = queue.data;

  const toggle = (rollIds: string[], on: boolean) =>
    setSelected((now) => {
      const next = new Set(now);
      for (const id of rollIds) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });

  const develop = useMutation({
    mutationFn: (rolls: { order_id: string; roll_id: string }[]) => call(() => lab.developed(rolls)),
    onSuccess: ({ results }) => {
      const moved = results.filter((r) => r.outcome === 'developed').length;
      const missed = results.length - moved;
      setAnnouncement(
        `Marked ${plural(moved, 'roll')} developed.${missed > 0 ? ` ${plural(missed, 'roll')} had already moved on or gone.` : ''}`,
      );
      setSelected(new Set());
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.labQueue() });
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.orders() });
    },
    onError: (e) => setAnnouncement((isApiError(e) && e.serverMessage) || 'Marking developed failed. Try again.'),
  });

  const developRolls = data?.develop.flatMap((g) => g.rolls) ?? [];
  const chosen = developRolls.filter((r) => selected.has(r.roll_id));

  return (
    <main className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.heading}>Admin — Lab</h1>
        <div className={styles.headerActions}>
          <Link to="/admin/orders" className={styles.secondaryBtn}>
            Orders
          </Link>
          <Link to="/admin/print-orders" className={styles.secondaryBtn}>
            Print Orders
          </Link>
          <Link to="/admin/film-stocks" className={styles.secondaryBtn}>
            Film Stocks
          </Link>
          <button type="button" onClick={logout} className={styles.secondaryBtn}>
            Logout
          </button>
        </div>
      </div>

      {data && (
        <p className={styles.counts}>
          {plural(data.counts.develop, 'roll')} to develop · {plural(data.counts.scan, 'roll')} to scan ·{' '}
          {plural(data.counts.ready, 'order')} to mark ready · negatives held for {plural(data.counts.held, 'roll')}
          {data.counts.overdue > 0 && <strong className={styles.overdue}> · {data.counts.overdue} overdue</strong>}
        </p>
      )}

      <div role="status" aria-live="polite" className={styles.announcement}>
        {announcement}
      </div>

      {queue.isLoading && <p className={styles.muted}>Loading the lab…</p>}
      {queue.error && (
        <p role="alert" className={styles.error}>
          Error loading the lab queues.
        </p>
      )}

      <FindByNumber />

      {data && (
        <>
          <section className={styles.section} aria-labelledby="develop-heading">
            <div className={styles.sectionHeader}>
              <h2 id="develop-heading" className={styles.sectionHeading}>
                To develop ({data.counts.develop})
              </h2>
              {developRolls.length > 0 && (
                <button
                  type="button"
                  className={styles.primaryBtn}
                  disabled={chosen.length === 0 || develop.isPending}
                  onClick={() => develop.mutate(chosen.map((r) => ({ order_id: r.order_id, roll_id: r.roll_id })))}
                >
                  {develop.isPending ? 'Saving…' : `Mark ${plural(chosen.length, 'roll')} developed`}
                </button>
              )}
            </div>
            {data.develop.length === 0 ? (
              <p className={styles.muted}>Nothing waiting to be developed.</p>
            ) : (
              data.develop.map((group) => (
                <DevelopGroup
                  key={group.process ?? 'unknown'}
                  process={group.process}
                  rolls={group.rolls}
                  selected={selected}
                  onToggle={toggle}
                />
              ))
            )}
          </section>

          <section className={styles.section} aria-labelledby="scan-heading">
            <h2 id="scan-heading" className={styles.sectionHeading}>
              To scan ({data.counts.scan})
            </h2>
            {data.scan.length === 0 ? (
              <p className={styles.muted}>Nothing waiting to be scanned.</p>
            ) : (
              <ul className={styles.orders}>
                {data.scan.map((group) => (
                  <li key={group.order_id} className={styles.orderGroup}>
                    <div className={styles.orderHeader}>
                      <strong>{group.order_number ?? group.order_id}</strong>
                      {group.customer_name && <span className={styles.muted}> · {group.customer_name}</span>}
                      <Link to={uploadLink(group.order_id)} className={styles.uploadLink}>
                        Upload scans
                      </Link>
                    </div>
                    <ul className={styles.list}>
                      {group.rolls.map((roll) => (
                        <li key={roll.roll_id}>
                          {roll.lab_roll_number && <strong className={styles.number}>{roll.lab_roll_number}</strong>}{' '}
                          {rollSummary(roll) || roll.roll_name}
                          <span className={styles.muted}>{roll.since && ` · waiting since ${shortDate(roll.since)}`}</span>
                          {roll.special_instructions && (
                            <strong className={styles.instructions}> · {roll.special_instructions}</strong>
                          )}
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className={styles.section} aria-labelledby="ready-heading">
            <h2 id="ready-heading" className={styles.sectionHeading}>
              To mark ready ({data.counts.ready})
            </h2>
            <p className={styles.muted}>Every roll scanned. Marking the order ready emails the customer their scans.</p>
            {data.ready.length === 0 ? (
              <p className={styles.muted}>No orders waiting to be marked ready.</p>
            ) : (
              <ul className={styles.orders}>
                {data.ready.map((group) => (
                  <li key={group.order_id} className={styles.orderGroup}>
                    <div className={styles.orderHeader}>
                      <strong>{group.order_number ?? group.order_id}</strong>
                      {group.customer_name && <span className={styles.muted}> · {group.customer_name}</span>}
                      <Link to={uploadLink(group.order_id)} className={styles.uploadLink}>
                        Mark ready
                      </Link>
                    </div>
                    <ul className={styles.list}>
                      {group.rolls.map((roll) => (
                        <li key={roll.roll_id}>
                          {roll.lab_roll_number && <strong className={styles.number}>{roll.lab_roll_number}</strong>}{' '}
                          {rollSummary(roll) || roll.roll_name}
                          <span className={styles.muted}>
                            {roll.marked_empty
                              ? ' · marked empty'
                              : roll.scanned_at && ` · scanned ${shortDate(roll.scanned_at)}`}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className={styles.section} aria-labelledby="held-heading">
            <h2 id="held-heading" className={styles.sectionHeading}>
              Negatives held ({data.counts.held})
            </h2>
            <p className={styles.muted}>
              Kept for a companion print or film order, for 90 days from when the lab finished. Once a hold runs out, the
              negatives are disposed of.
            </p>
            {data.held.length === 0 ? (
              <p className={styles.muted}>No negatives are being held.</p>
            ) : (
              <ul className={styles.orders}>
                {data.held.map((roll) => (
                  <li key={roll.roll_id} className={roll.overdue ? `${styles.orderGroup} ${styles.overdueRow}` : styles.orderGroup}>
                    <div className={styles.orderHeader}>
                      <strong>{roll.roll_name}</strong> · <Customer roll={roll} />
                    </div>
                    <p className={styles.heldFacts}>
                      {roll.overdue ? (
                        <strong className={styles.overdue}>Hold ended {roll.held_until && shortDate(roll.held_until)}</strong>
                      ) : (
                        <>Held until {roll.held_until && shortDate(roll.held_until)}</>
                      )}
                      <span className={styles.muted}>
                        {' '}
                        · {roll.reminder_sent_at ? `reminder sent ${shortDate(roll.reminder_sent_at)}` : 'no reminder sent yet'}
                      </span>
                    </p>
                    <NegativesActions
                      orderId={roll.order_id}
                      rollId={roll.roll_id}
                      rollName={roll.roll_name}
                      negatives={{ state: 'held', until: roll.held_until ?? '', overdue: roll.overdue }}
                      plan="hold"
                      labDone
                      onDone={(_roll, message) => setAnnouncement(message)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </section>

          <OlderHolds onDone={setAnnouncement} />
        </>
      )}
    </main>
  );
}
